import React, { useEffect, useRef } from "react";
import ReactEcharts from "echarts-for-react";
import { useSelector } from "react-redux";

import { inverseTransform, kPaToPF, logTransform } from "./Gauge2";

import {
  colors,
  colorsSoilMoisture,
  colorsSoilWaterTension,
  colorsPulse,
  colorsDendrometer,
} from "@helper/colors";
import { getMoistureContentCapEnabled } from "@helper/moistureContentDisplay";
import { useDarkMode } from "@hooks/useDarkMode";
import { buildSparseGaugeLabels } from "./gaugeLabels";
import { Button } from "react-bootstrap";

export default function MoistureGauge({
  tree,
  sensor,
  t,
  soilWaterTensionType,
  setSoilWaterTensionType,
  layoutKey,
  layoutWidth,
  calibrationSpeciesName,
}) {
  const gaugeContainerRef = useRef(null);
  const gaugeChartRef = useRef(null);
  const authUser = useSelector((state) => state.auth?.user);
  const moistureContentCapEnabled = getMoistureContentCapEnabled(authUser);
  const { isDarkMode } = useDarkMode();
  const textColor = isDarkMode ? "#A1B5C8" : "#333";
  let COLORS = colors;
  let value = 0;
  let sensorType = "pulse";
  let pulseColorFractions = null;
  let swtRawKpaValues = null;

  if (sensor) {
    sensorType = sensor.pulse_sensor
      ? "pulse"
      : sensor.soil_moisture_sensor
      ? "soil_moisture"
      : sensor.soil_water_tension_sensor
      ? "soil_water_tension"
      : "dendrometer";
    COLORS =
      sensorType == "soil_moisture"
        ? colorsSoilMoisture(
            tree.soil ? tree.soil.pwp : 10,
            tree.soil ? tree.soil.max_nf : 25,
            tree.soil ? tree.soil.max_f : 45
          )
        : sensorType == "soil_water_tension"
        ? colorsSoilWaterTension(tree.soil)
        : sensorType == "pulse"
        ? colorsPulse(tree.blue_threshold, tree.green_threshold, tree.yellow_threshold)
        : sensorType == "dendrometer"
        ? colorsDendrometer()
        : colors;

    // Capture raw threshold data before transformations
    pulseColorFractions = sensorType === "pulse" ? COLORS.map(([f]) => f) : null;
    swtRawKpaValues = sensorType === "soil_water_tension" ? COLORS.map(([kPa]) => kPa) : null;

    if (sensorType == "soil_water_tension" && soilWaterTensionType == "pF") {
      COLORS = COLORS.map((values) => {
        return [values[0] == 0 ? 1 : 1 - kPaToPF(values[0]) / kPaToPF(-1585), values[1]];
      });
    }
    if (sensorType == "soil_water_tension" && soilWaterTensionType == "kPa") {
      COLORS = COLORS.map((values) => {
        return [logTransform(values[0], -2000, 0), values[1]];
      });
    }
    const rawValue =
      sensorType == "pulse"
        ? sensor.pulse_sensor.moisture_content
        : sensorType == "soil_moisture"
        ? sensor.soil_moisture_sensor.soil_moisture
        : sensorType == "soil_water_tension"
        ? sensor.soil_water_tension_sensor.soil_water_tension
        : sensorType == "dendrometer"
        ? sensor.dendrometer_sensor.drought_days
        : 0;
    // Apply capping for pulse sensor moisture content if user setting enabled
    value = sensorType == "pulse" && moistureContentCapEnabled ? Math.min(100, rawValue) : rawValue;
  } else {
    // Apply capping for tree moisture content (when no sensor) if user setting enabled
    value = moistureContentCapEnabled
      ? Math.min(100, tree.moisture_content)
      : tree.moisture_content;
  }

  // check for valid points
  if (sensorType == "soil_water_tension") {
    if (value == 255) {
      return (
        <h3>
          {t("unknown")} {t("status")}
        </h3>
      );
    }
  }
  const valueType = sensor
    ? sensorType == "pulse"
      ? "moisture_content"
      : sensorType == "soil_moisture"
      ? "soil_moisture"
      : sensorType == "soil_water_tension"
      ? "soil_water_tension"
      : sensorType == "dendrometer"
      ? "drought_days"
      : ""
    : "moisture_content";
  let valueUnit = "%";
  let max = 100;
  let min = 0;

  if (sensorType == "dendrometer") {
    min = 0;
    max = 14;
    valueUnit = "days";
  } else if (soilWaterTensionType == "kPa") {
    min = sensorType == "soil_water_tension" ? 0 : 0;
    max = sensorType == "soil_water_tension" ? 1 : 100;
    valueUnit = sensorType == "soil_water_tension" ? "kPa" : "%";
  } else {
    min = sensorType == "soil_water_tension" ? 4.2 : 0;
    max = sensorType == "soil_water_tension" ? 0 : 100;
    valueUnit = sensorType == "soil_water_tension" ? "pF" : "%";
  }
  // Dynamically expand gauge max when moisture content cap is disabled
  if (!moistureContentCapEnabled && (sensorType === "pulse" || !sensor)) {
    const candidates = [100];
    if (Number.isFinite(value)) candidates.push(value);
    if (sensor && sensorType === "pulse") {
      if (tree.blue_threshold != null) candidates.push(tree.blue_threshold);
      if (tree.green_threshold != null) candidates.push(tree.green_threshold);
      if (tree.yellow_threshold != null) candidates.push(tree.yellow_threshold);
    }
    const rawMax = Math.max(...candidates);
    max = Math.ceil(rawMax / 10) * 10;
  }

  // Re-normalize pulse color fractions when gauge max exceeds 100
  if (max > 100 && sensorType === "pulse") {
    COLORS = COLORS.map(([fraction, color]) => [(fraction * 100) / max, color]);
    if (COLORS.length > 0 && COLORS[COLORS.length - 1][0] < 1) {
      COLORS.push([1, COLORS[COLORS.length - 1][1]]);
    }
  }

  // Compute sparse gauge labels for non-dendrometer sensors
  let sparseLabels = null;
  if (sensor && sensorType !== "dendrometer") {
    let labelEntries = [];
    if (sensorType === "pulse") {
      // Show only yellow (red→yellow) and green (yellow→green) threshold labels
      const yellowGaugeValue = pulseColorFractions[0] * 100;
      const greenGaugeValue = pulseColorFractions[1] * 100;
      labelEntries = [
        { gaugeValue: min, displayText: String(Math.round(min)), priority: 3 },
        {
          gaugeValue: yellowGaugeValue,
          displayText: String(Math.round(yellowGaugeValue)),
          priority: 1,
        },
        {
          gaugeValue: greenGaugeValue,
          displayText: String(Math.round(greenGaugeValue)),
          priority: 1,
        },
        { gaugeValue: max, displayText: String(Math.round(max)), priority: 2 },
      ];
    } else if (sensorType === "soil_moisture") {
      // Use same values + falsy-value fallback as colorsSoilMoisture
      const rawPwp = tree.soil ? tree.soil.pwp : 10;
      const rawMaxNf = tree.soil ? tree.soil.max_nf : 25;
      const rawMaxF = tree.soil ? tree.soil.max_f : 45;
      const effectivePwp = rawPwp || 10;
      const effectiveMaxNf = rawMaxNf || 25;
      const effectiveMaxF = rawMaxF || 45;
      labelEntries = [
        { gaugeValue: 0, displayText: "0", priority: 3 },
        {
          gaugeValue: effectivePwp,
          displayText: String(Math.round(effectivePwp)),
          priority: 1,
        },
        {
          gaugeValue: effectiveMaxNf,
          displayText: String(Math.round(effectiveMaxNf)),
          priority: 1,
        },
        {
          gaugeValue: effectiveMaxF,
          displayText: String(Math.round(effectiveMaxF)),
          priority: 1,
        },
        { gaugeValue: 100, displayText: "100", priority: 2 },
      ];
    } else if (sensorType === "soil_water_tension") {
      // Pair raw kPa values with their transformed gauge positions
      const swtPairs = swtRawKpaValues.map((rawKpa, i) => ({
        rawValue: rawKpa,
        gaugeValue: min + COLORS[i][0] * (max - min),
      }));
      if (soilWaterTensionType === "kPa") {
        labelEntries = [
          { gaugeValue: min, displayText: "-2000", priority: 3 },
          ...swtPairs.map((t) => ({
            gaugeValue: t.gaugeValue,
            displayText: String(Math.round(t.rawValue)),
            priority: 1,
          })),
          { gaugeValue: max, displayText: "0", priority: 2 },
        ];
      } else {
        // pF mode
        labelEntries = [
          { gaugeValue: min, displayText: String(min), priority: 3 },
          ...swtPairs.map((t) => ({
            gaugeValue: t.gaugeValue,
            displayText: t.rawValue === 0 ? "0" : String(Number(kPaToPF(t.rawValue).toFixed(1))),
            priority: 1,
          })),
          { gaugeValue: max, displayText: "0", priority: 2 },
        ];
      }
    }
    sparseLabels = buildSparseGaugeLabels(labelEntries, min, max);
  }

  const title =
    sensorType === "dendrometer" ? `${t(valueType)}` : `${t(valueType)} in ${valueUnit}`;
  const option = {
    title: {
      text: title,
      show: true,
      x: "center",
      y: "top",
      textStyle: { color: textColor },
    },
    series: [
      {
        type: "gauge",
        min: min,
        max: max,
        splitNumber: sparseLabels
          ? sparseLabels.splitNumber
          : sensorType == "dendrometer"
          ? 14
          : undefined,
        radius: "80%",
        center: ["50%", "50%"],
        axisLine: {
          lineStyle: {
            width: 20,
            color: COLORS,
          },
        },
        splitLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: sparseLabels
          ? {
              formatter: sparseLabels.formatter,
              fontSize: 10,
              color: textColor,
            }
          : {
              color: textColor,
              formatter: function (value) {
                let realValue = value;
                if (sensorType == "dendrometer") {
                  realValue = Math.round(value);
                }
                if (sensorType == "soil_water_tension") {
                  if (soilWaterTensionType == "kPa") {
                    realValue = inverseTransform(value, -2000, 0).toFixed(0);
                  } else {
                    realValue = Math.max(Number(kPaToPF(value).toFixed(1)), 0);
                  }
                }
                return `${realValue}`;
              },
            },
        pointer: {
          length: 100,
        },
        detail: {
          show: true,
          color: textColor,
          formatter: function (value) {
            let realValue = value;
            if (sensorType == "soil_water_tension") {
              if (soilWaterTensionType == "kPa") {
                realValue = inverseTransform(value, -2000, 0).toFixed(0);
              } else {
                realValue = Math.max(Number(kPaToPF(value).toFixed(1)), 0);
              }
            }
            return realValue;
          },
        },
        data: [
          {
            value:
              soilWaterTensionType == "kPa"
                ? sensorType == "soil_water_tension"
                  ? logTransform(value, -2000, 0)
                  : value
                : sensorType == "soil_water_tension"
                ? Math.max(kPaToPF(value).toFixed(1), 0)
                : sensorType == "dendrometer"
                ? Math.max(0, Math.min(14, Number(value)))
                : value.toFixed(0),
          },
        ],
      },
    ],
  };
  const style = {
    height: "280px",
    width: "100%",
  };
  const handleClick = () => {
    if (soilWaterTensionType != "pF") {
      setSoilWaterTensionType("pF");
    } else {
      setSoilWaterTensionType("kPa");
    }
  };

  useEffect(() => {
    const resizeChart = () => {
      const chart = gaugeChartRef.current?.getEchartsInstance?.();
      chart?.resize?.();
    };

    requestAnimationFrame(resizeChart);
    const timer = setTimeout(resizeChart, 150);
    window.addEventListener("resize", resizeChart);

    if (typeof ResizeObserver === "undefined" || !gaugeContainerRef.current) {
      return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", resizeChart);
      };
    }

    const observer = new ResizeObserver(() => resizeChart());
    observer.observe(gaugeContainerRef.current);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", resizeChart);
      observer.disconnect();
    };
  }, [sensor?.id, soilWaterTensionType, layoutKey, layoutWidth]);

  return (
    <div ref={gaugeContainerRef} style={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
      {sensorType == "soil_water_tension" && (
        <Button onClick={handleClick}>{soilWaterTensionType == "kPa" ? "pF" : "kPa"}</Button>
      )}
      <ReactEcharts ref={gaugeChartRef} option={option} style={style} notMerge />
      {sensorType === "pulse" && calibrationSpeciesName && (
        <div
          style={{
            textAlign: "center",
            fontSize: "11px",
            color: "#6c757d",
            marginTop: "-8px",
            fontStyle: "italic",
          }}
          title={t("calibration_species_tooltip")}
        >
          {t("calibration_species")}: <em>{calibrationSpeciesName}</em>
        </div>
      )}
    </div>
  );
}

export function Gauge({ value, min, max, COLORS }) {
  const option = {
    series: [
      {
        type: "gauge",
        min: min,
        max: max,
        radius: "90%",
        center: ["50%", "50%"],
        axisLine: {
          lineStyle: {
            width: 20,
            color: COLORS,
          },
        },
        splitLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        pointer: {
          length: 50,
        },
        detail: {
          show: true,
        },
        data: [
          {
            value: value,
          },
        ],
      },
    ],
  };
  return <ReactEcharts option={option} style={{ height: "120%", width: "120%" }} notMerge />;
}
